import React from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from './context/ThemeContext';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';

import SignInScreen from './components/SignInScreen/SignInScreen';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import UpgradePage from './components/account/UpgradePage';
import BillingPage from './components/account/BillingPage';
import UpgradeStatusPage from './components/account/UpgradeStatusPage';
import PrivacyPage from './components/account/PrivacyPage';
import Background from './components/Background/Background';
import { isApplePlatform } from './utils/browserApi';
import { isNativeApp, nativeEmbed, nativePlatform } from './utils/nativeApp';
import './styles/global.css';
import './styles/toast.css';
import { ToastProvider } from './context/ToastContext';
import rhemnLogo from './assets/rhemn-logo.png';
import { trackAppOpen, trackFirstRun } from './services/analytics';
import { runLegacyTourCheck } from './features/Onboarding/ExploreTour/tourState';

// ── Platform-split shells ─────────────────────────────────────────────────────
// The two builds share one codebase but ship different critical paths:
//
// - Extension build (REACT_APP_PLATFORM=extension, set in .env.production):
//   MainContent is loaded as its own chunk. The NativeShell branch below is
//   statically dead in production, so the extension never loads native code.
// - iOS build (REACT_APP_PLATFORM=ios, set by scripts/build-ios.js): the
//   MainContent is a lazy chunk that is never fetched (NativeShell replaces it),
//   and the heavy panels
//   (Tiptap/Notes, Todo, Insights) stay out of the cold-start critical path.
const MainContentImpl = React.lazy(() => import('./components/MainContent'));
let NativeShell: React.LazyExoticComponent<React.ComponentType> | null = null;
// iOS 26: the Insights system sheet's second web view (src/native/InsightsEmbed.tsx).
let InsightsEmbed: React.LazyExoticComponent<React.ComponentType> | null = null;

if (process.env.REACT_APP_PLATFORM === 'ios') {
  NativeShell = React.lazy(() => import('./native/NativeShell'));
  InsightsEmbed = React.lazy(() => import('./native/InsightsEmbed'));

  // The embed is transparent: the native sheet's Liquid Glass shows through.
  if (typeof document !== 'undefined' && isNativeApp() && nativeEmbed()) {
    const rootEl = document.documentElement;
    rootEl.classList.add('is-native', 'is-native-embed');
    rootEl.style.background = 'transparent';
    document.body.style.background = 'transparent';
  }

  // Native boot chrome, applied before the shell chunk arrives so the launch
  // screen → first web paint is one continuous #121218 surface (spec §7.6).
  // Guarded at runtime: a plain browser opening the iOS bundle stays unstyled.
  if (typeof document !== 'undefined' && isNativeApp() && !nativeEmbed()) {
    const rootEl = document.documentElement;
    rootEl.classList.add('is-native');
    if (nativePlatform() === 'ipados') rootEl.classList.add('is-native-ipad');
    rootEl.style.backgroundColor = '#121218';
    document.body.style.backgroundColor = '#121218';

    // Warm the background photo: start the cached image download before React
    // mounts so the crossfade begins as early as possible (perf budget §7.6).
    void import('./services/unsplashApi')
      .then(async (unsplash) => {
        const cached = await unsplash.getCachedImage();
        if (cached) new Image().src = unsplash.getImageUrl(cached);
      })
      .catch(() => { /* best-effort warm-up only */ });
  }
}

// Safety rule for local testing:
// - FORCE_PRO alone does NOT bypass auth (keeps API flows testable with a real user).
// - PREVIEW_WITHOUT_AUTH bypasses auth regardless of FORCE_PRO (used for screenshots/demos).
const PREVIEW_WITHOUT_AUTH = process.env.REACT_APP_PREVIEW_WITHOUT_AUTH === 'true';

// const DebugAuthBanner: React.FC<{ user: unknown; loading: boolean }> = ({ user, loading }) => {
//   if (process.env.NODE_ENV !== 'development') return null;
//   if (process.env.REACT_APP_DEBUG_AUTH !== 'true') return null;

//   return (
//     <div style={{ position: 'fixed', bottom: 12, left: 12, zIndex: 9999, background: 'rgba(2,6,23,.85)', color: '#fff', border: '1px solid rgba(255,255,255,.2)', borderRadius: 10, padding: '8px 10px', fontSize: 12 }}>
//       <div>auth: {loading ? 'loading' : user ? 'signed-in' : 'signed-out'}</div>
//       <div>forcePro: {String(process.env.REACT_APP_FORCE_PRO === 'true')}</div>
//       <div>previewBypass: {String(PREVIEW_WITHOUT_AUTH)}</div>
//       <div>profileApi: {String(!!process.env.REACT_APP_PROFILE_API_BASE_URL)}</div>
//       <div>graphqlApi: {String(!!process.env.REACT_APP_GRAPHQL_API_URL)}</div>
//     </div>
//   );
// };

const AppContent: React.FC = () => {
  const { t } = useTranslation('common');
  const { user, loading } = useAuth();
  const [timedOut, setTimedOut] = React.useState(false);
  const path = window.location.pathname;
  const isAccountPath = path.startsWith('/upgrade') || path.startsWith('/billing') || path === '/privacy';

  React.useEffect(() => {
    if (!loading) return;
    const id = window.setTimeout(() => setTimedOut(true), 10_000);
    return () => window.clearTimeout(id);
  }, [loading]);

  // First-party analytics: count every new-tab open (anonymous, aggregate-only).
  // Fires once per app load, regardless of auth — anonymous free users count too.
  // Skipped on the hosted account pages (/upgrade, /billing, /privacy).
  React.useEffect(() => {
    if (isAccountPath) return;
    trackAppOpen();
  }, [isAccountPath]);

  // First-session funnel (W35 S1) — all three calls are once-EVER no-ops after
  // their first fire (persistent guards inside trackFirstRun), and no-ops
  // entirely for clients that predate the instrumentation.
  React.useEffect(() => {
    // timedOut matters: an auth hang >10s ALSO drops the user on the wall, and
    // that broken path is precisely what this funnel exists to catch.
    if (isAccountPath || (loading && !timedOut)) return;
    // The loading gate has resolved into real content (wall or dashboard).
    // Double-rAF = the frame after this content actually painted.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => trackFirstRun('paint'));
    });
    if (!user) trackFirstRun('signin_shown'); // the hard wall is what renders below
    else trackFirstRun('signin_dismissed'); // only fires if signin_shown fired first
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [isAccountPath, loading, timedOut, user]);

  // Account pages render BEFORE the auth loading gate. They never need a resolved
  // extension auth session: /upgrade signs itself in from the `ct` token, and
  // /upgrade/success, /upgrade/cancel, /billing, /privacy are self-contained. Gating
  // them behind auth made the post-Stripe success page sit blank until the 10s auth
  // timeout fired (the Stripe return URL carries no `ct` token, so bgAuthBridge.getUser()
  // hangs in the web context). Rendering them first makes the success page instant.
  if (isAccountPath) {
    if (path === '/privacy') return <PrivacyPage />;
    // Defense in depth (Apple 3.1.1/3.1.3): a Safari/iOS App Store build must never
    // expose a purchase or billing-management surface. The upgrade/billing pages are
    // the hosted account web app (account.rhemn.com), where isApplePlatform() is
    // false, so this guard only trips if a Safari *extension* build or the iOS
    // native app ever routes here.
    if (isApplePlatform() && (path.startsWith('/upgrade') || path.startsWith('/billing'))) {
      return (
        <div className="app-loading" role="status">
          <p style={{ maxWidth: 360, textAlign: 'center', lineHeight: 1.6, opacity: 0.86 }}>
            {t('app.safariManageOnWeb')}
          </p>
        </div>
      );
    }
    if (path === '/billing') return <BillingPage />;
    if (path === '/upgrade/success') return <UpgradeStatusPage kind="success" />;
    if (path === '/upgrade/cancel') return <UpgradeStatusPage kind="cancel" />;
    return <UpgradePage />;
  }

  if (loading && !timedOut) {
    return (
      <div className="app-loading" role="status" aria-label={t('app.loading_aria')}>
        {/* Background photo renders at z-index -1 — visible through this overlay */}
        <Background />

        {/* Logo — same size as sign-in screen for visual continuity */}
        <img
          src={rhemnLogo}
          alt={t('app.logo_alt')}
          className="app-loading__logo"
          aria-hidden="true"
        />

        {/*
          Crescent arc indicator.
          SVG arc: r=18, circumference ≈ 113.1px.
          220° arc = (220/360) * 113.1 ≈ 69px dash, rest gap.
          Rotated -110° so gap sits at the bottom (symmetrical).
          No spin — only the CSS breathe animation on opacity.
        */}
        <div className="app-loading__arc-wrap" aria-hidden="true">
          <svg
            width="44"
            height="44"
            viewBox="0 0 44 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              className="app-loading__arc"
              cx="22"
              cy="22"
              r="18"
              stroke="rgba(255,255,255,0.72)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray="69 44"
              transform="rotate(-110 22 22)"
            />
          </svg>
        </div>
      </div>
    );
  }

  if (!user && !PREVIEW_WITHOUT_AUTH) {
    return (
      <>
        <SignInScreen />
        {/* <DebugAuthBanner user={user} loading={loading} /> */}
      </>
    );
  }

  // iOS/iPadOS native app: the tab-bar shell replaces the new-tab dashboard.
  // Statically dead on the extension build (see the platform split above).
  if (NativeShell && isNativeApp()) {
    return (
      <React.Suspense fallback={null}>
        <NativeShell />
      </React.Suspense>
    );
  }

  return (
    <>
      <React.Suspense fallback={null}>
        <MainContentImpl />
      </React.Suspense>
      {/* <DebugAuthBanner user={user} loading={loading} /> */}
    </>
  );
};

/**
 * The Insights embed root: only auth (entitlement needs the user) and the
 * panel. No AppProvider — a second instance would run its sync engine against
 * the main app's storage — and no analytics, which would double-count opens.
 */
const EmbedGate: React.FC = () => {
  const { loading } = useAuth();
  if (loading || !InsightsEmbed) return null;
  return (
    <React.Suspense fallback={null}>
      <InsightsEmbed />
    </React.Suspense>
  );
};

const EmbedApp: React.FC = () => (
  <ErrorBoundary>
    {/* Toasts: the verse's copy action confirms with one (InsightsVerse). */}
    <ToastProvider>
      <AuthProvider>
        <EmbedGate />
      </AuthProvider>
    </ToastProvider>
  </ErrorBoundary>
);

const App: React.FC = () => (InsightsEmbed && nativeEmbed() ? <EmbedApp /> : <FullApp />);

const FullApp: React.FC = () => {
  // Explore Mode legacy check (plan §2.2). A lazy state initializer runs during
  // the FIRST render, before any effect in the tree — in particular before
  // AppContext's identity guard stamps auth.lastUid. Reading it any later would
  // mistake every brand-new user for an existing one.
  React.useState(runLegacyTourCheck);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <ThemeProvider>
          <AuthProvider>
            <AppProvider>
              <AppContent />
            </AppProvider>
          </AuthProvider>
        </ThemeProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
